/**
 * Expense Entry Page
 * Enter and manage expense entries at various levels (Country, Branch, Department, Employee)
 */
import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { api, toast } from "../App";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  DollarSign,
  Building2,
  Users,
  Globe,
  ChevronsUpDown,
  Check,
  Filter,
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function ExpenseEntry({ user }) {
  const [expenses, setExpenses] = useState([]);
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [countryConfigs, setCountryConfigs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterBranch, setFilterBranch] = useState("all");
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [openEmployeeCombobox, setOpenEmployeeCombobox] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    level: "employee",
    country: "India",
    branch: "",
    department: "",
    employee_id: "",
    expense_head_id: "",
    amount: "",
    currency: "INR",
    remarks: "",
  });

  const departments = ["Projects", "Management", "Sales", "HR", "Finance", "Admin"];
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  useEffect(() => {
    fetchData();
    fetchBranches();
  }, [filterMonth, filterYear, filterCountry, filterBranch]);

  const fetchBranches = async () => {
    try {
      const response = await api.get("/branches");
      setBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      let expenseUrl = `/expenses?period_month=${filterMonth}&period_year=${filterYear}`;
      if (filterCountry && filterCountry !== "all") expenseUrl += `&country=${filterCountry}`;
      if (filterBranch && filterBranch !== "all") expenseUrl += `&branch=${filterBranch}`;
      
      const [expensesRes, headsRes, configsRes, employeesRes] = await Promise.all([
        api.get(expenseUrl),
        api.get("/expense-heads"),
        api.get("/country-config"),
        api.get("/employees"),
      ]);
      
      setExpenses(expensesRes.data);
      setExpenseHeads(headsRes.data.filter(h => h.is_active));
      setCountryConfigs(configsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.expense_head_id || !formData.amount || !formData.country) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, formData);
        toast.success("Expense updated successfully");
      } else {
        await api.post("/expenses", formData);
        toast.success("Expense created successfully");
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save expense");
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      period_month: expense.period_month,
      period_year: expense.period_year,
      level: expense.level,
      country: expense.country,
      branch: expense.branch || "",
      department: expense.department || "",
      employee_id: expense.employee_id || "",
      expense_head_id: expense.expense_head_id,
      amount: expense.amount,
      currency: expense.currency,
      remarks: expense.remarks || "",
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this expense?")) return;
    
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({
      period_month: filterMonth,
      period_year: filterYear,
      level: "employee",
      country: "India",
      branch: "",
      department: "",
      employee_id: "",
      expense_head_id: "",
      amount: "",
      currency: "INR",
      remarks: "",
    });
  };

  const openAddDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  // Update currency when country changes
  const handleCountryChange = (country) => {
    const config = countryConfigs.find(c => c.country === country);
    setFormData({
      ...formData,
      country,
      currency: config?.currency || "USD"
    });
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case "country": return <Globe className="h-4 w-4" />;
      case "branch": return <Building2 className="h-4 w-4" />;
      case "department": return <Users className="h-4 w-4" />;
      case "employee": return <Users className="h-4 w-4" />;
      default: return null;
    }
  };

  const getLevelBadge = (level) => {
    const colors = {
      country: "bg-blue-100 text-blue-800",
      branch: "bg-purple-100 text-purple-800",
      department: "bg-green-100 text-green-800",
      employee: "bg-orange-100 text-orange-800",
    };
    return colors[level] || "bg-gray-100 text-gray-800";
  };

  const canManage = user?.access_level === "Manager" || user?.access_level === "Admin";

  // Calculate totals
  const totalAmount = expenses.reduce((sum, e) => sum + (e.usd_amount || 0), 0);
  const directTotal = expenses.filter(e => e.expense_type === "Direct").reduce((sum, e) => sum + (e.usd_amount || 0), 0);
  const indirectTotal = expenses.filter(e => e.expense_type === "Indirect").reduce((sum, e) => sum + (e.usd_amount || 0), 0);

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Expense Entry</h1>
            <p className="text-gray-500">Track and manage company expenses</p>
          </div>
          {canManage && (
            <Button onClick={openAddDialog} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Expenses</p>
                  <p className="text-2xl font-bold">${totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Direct Costs</p>
                  <p className="text-2xl font-bold text-green-600">${directTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <Users className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Indirect Costs</p>
                  <p className="text-2xl font-bold text-purple-600">${indirectTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <Building2 className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Entry Count</p>
                  <p className="text-2xl font-bold">{expenses.length}</p>
                </div>
                <Filter className="h-8 w-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Month</Label>
                <Select value={filterMonth.toString()} onValueChange={(v) => setFilterMonth(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Select value={filterYear.toString()} onValueChange={(v) => setFilterYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Select value={filterCountry} onValueChange={setFilterCountry}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countryConfigs.map(c => (
                      <SelectItem key={c.country} value={c.country}>{c.country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Branch</Label>
                <Select value={filterBranch} onValueChange={setFilterBranch}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.name}>{b.name} ({b.country})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expense Table */}
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Level</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Expense Head</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">USD</TableHead>
                  {canManage && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No expenses found for this period
                    </TableCell>
                  </TableRow>
                ) : (
                  expenses.map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>
                        <Badge className={getLevelBadge(exp.level)}>
                          {exp.level}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{exp.country}</div>
                          {exp.branch && <div className="text-gray-500">{exp.branch}</div>}
                          {exp.department && <div className="text-gray-400 text-xs">{exp.department}</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {exp.employee_name || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{exp.expense_head_name}</div>
                          <div className="text-gray-500 text-xs">{exp.expense_head_code}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={exp.expense_type === "Direct" ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"}>
                          {exp.expense_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {exp.currency} {exp.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        ${exp.usd_amount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </TableCell>
                      {canManage && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(exp)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {user?.access_level === "Admin" && (
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(exp.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingExpense ? "Edit Expense" : "Add Expense"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              {/* Period */}
              <div className="space-y-2">
                <Label>Month *</Label>
                <Select value={formData.period_month.toString()} onValueChange={(v) => setFormData({...formData, period_month: parseInt(v)})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year *</Label>
                <Select value={formData.period_year.toString()} onValueChange={(v) => setFormData({...formData, period_year: parseInt(v)})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Level & Country */}
              <div className="space-y-2">
                <Label>Entry Level *</Label>
                <Select value={formData.level} onValueChange={(v) => setFormData({...formData, level: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="country">Country Level</SelectItem>
                    <SelectItem value="branch">Branch Level</SelectItem>
                    <SelectItem value="department">Department Level</SelectItem>
                    <SelectItem value="employee">Employee Level</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Country *</Label>
                <Select value={formData.country} onValueChange={handleCountryChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countryConfigs.map(c => (
                      <SelectItem key={c.country} value={c.country}>{c.country} ({c.currency})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Branch & Department */}
              {(formData.level === "branch" || formData.level === "department" || formData.level === "employee") && (
                <div className="space-y-2">
                  <Label>Branch {formData.level !== "country" ? "*" : ""}</Label>
                  <Select value={formData.branch} onValueChange={(v) => setFormData({...formData, branch: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map(b => (
                        <SelectItem key={b.id} value={b.name}>{b.name} ({b.country})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(formData.level === "department" || formData.level === "employee") && (
                <div className="space-y-2">
                  <Label>Department *</Label>
                  <Select value={formData.department} onValueChange={(v) => setFormData({...formData, department: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Employee (for employee level) */}
              {formData.level === "employee" && (
                <div className="space-y-2 col-span-2">
                  <Label>Employee *</Label>
                  <Popover open={openEmployeeCombobox} onOpenChange={setOpenEmployeeCombobox}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between overflow-hidden">
                        <span className="truncate">
                          {formData.employee_id
                            ? (() => {
                                const emp = employees.find(e => e.id === formData.employee_id);
                                return emp ? `${emp.first_name} ${emp.last_name} - ${emp.department}` : "Select employee...";
                              })()
                            : "Select employee..."}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search employee..." />
                        <CommandList>
                          <CommandEmpty>No employee found.</CommandEmpty>
                          <CommandGroup>
                            {employees
                              .filter(e => !formData.department || e.department === formData.department)
                              .map((emp) => (
                                <CommandItem
                                  key={emp.id}
                                  value={`${emp.first_name} ${emp.last_name} ${emp.department}`}
                                  onSelect={() => {
                                    setFormData({ ...formData, employee_id: emp.id });
                                    setOpenEmployeeCombobox(false);
                                  }}
                                >
                                  <Check className={cn("mr-2 h-4 w-4", formData.employee_id === emp.id ? "opacity-100" : "opacity-0")} />
                                  {emp.first_name} {emp.last_name} - {emp.department}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Expense Head */}
              <div className="space-y-2 col-span-2">
                <Label>Expense Head *</Label>
                <Select value={formData.expense_head_id} onValueChange={(v) => setFormData({...formData, expense_head_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select expense head" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseHeads.map(h => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.code} - {h.name} ({h.expense_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label>Amount ({formData.currency}) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || ""})}
                  placeholder="Enter amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Input value={formData.currency} disabled className="bg-gray-100" />
              </div>

              {/* Remarks */}
              <div className="space-y-2 col-span-2">
                <Label>Remarks</Label>
                <Textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  placeholder="Optional notes..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
                {editingExpense ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
